import React, { useEffect, useState } from "react";
import {
  IoSearch,
  IoPeopleOutline,
  IoKeyOutline,
  IoPersonAddOutline,
} from "react-icons/io5";
import { FiEdit, FiLock, FiUserCheck, FiUserX, FiPlus, FiX } from "react-icons/fi";
import { motion, AnimatePresence } from "framer-motion";
import Pagination from "@mui/material/Pagination";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import useAuth from "../../useAuth";
import { toast } from "react-toastify";
import { server } from "../../server";
import { useDebounce } from "../../hooks/useDebounce";
import LoadingComponent from "../../Components/LoadingComponent";

export default function UsersList() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [usersList, setUsersList] = useState([]);
  const [usersCount, setUsersCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchParams, setSearchParams] = useState("");
  const [userTypeFilter, setUserTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Modals state
  const [changePasswordUser, setChangePasswordUser] = useState(null);
  const [editProfileUser, setEditProfileUser] = useState(null);

  const debounceSearch = useDebounce(searchParams, 400);

  useEffect(() => {
    getAllUsers();
  }, [pageNo, pageSize, debounceSearch, userTypeFilter, statusFilter]);

  const getAllUsers = async () => {
    try {
      setLoading(true);
      const params = {
        pageNo,
        pageSize,
      };
      if (debounceSearch) params.searchParams = debounceSearch;
      if (userTypeFilter) params.userType = userTypeFilter;
      if (statusFilter !== "") params.disabled = statusFilter === "disabled";

      const { data } = await axios.get(`${server}/auth/getAllUsers`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      });

      setUsersList(data?.data || []);
      setUsersCount(data?.count || data?.data?.length || 0);
      setLoading(false);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.response?.data || e?.message);
      setLoading(false);
    }
  };

  const handleToggleStatus = async (targetUser) => {
    const action = targetUser.disabled ? "enable" : "disable";
    const confirm = window.confirm(
      `Are you sure you want to ${action} ${targetUser.fullName || targetUser.email}?`
    );
    if (!confirm) return;

    try {
      setLoading(true);
      await axios.post(
        `${server}/auth/updateUserAccesibility`,
        null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          params: {
            userId: targetUser.id,
            disabled: !targetUser.disabled,
          },
        }
      );
      toast.success(
        `User ${targetUser.fullName || targetUser.email} has been ${
          !targetUser.disabled ? "disabled" : "enabled"
        }!`
      );
      getAllUsers();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen p-6">
      <motion.div
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4 }}
        className="max-w-7xl mx-auto"
      >
        {/* Top Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 text-blue-600 rounded-2xl shadow-sm">
              <IoPeopleOutline className="text-3xl" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
                Users Management
              </h1>
              <p className="text-sm text-gray-500">
                Manage all registered users, roles, account status, and passwords
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate("/my/registerUser")}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-5 py-2.5 rounded-xl shadow-md hover:shadow-lg transition-all"
          >
            <IoPersonAddOutline className="text-lg" />
            <span>Register New User</span>
          </button>
        </div>

        {/* Filters and Search Bar */}
        <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 mb-6 flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Search */}
          <div className="relative w-full md:w-96">
            <input
              type="text"
              placeholder="Search by name, email, phone, org..."
              value={searchParams}
              onChange={(e) => setSearchParams(e.target.value)}
              className="w-full h-11 pl-11 pr-4 rounded-xl border border-gray-200 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none bg-gray-50 text-sm transition"
            />
            <IoSearch className="absolute left-3.5 top-3.5 text-gray-400 text-lg" />
            {searchParams && (
              <button
                onClick={() => setSearchParams("")}
                className="absolute right-3.5 top-3.5 text-gray-400 hover:text-gray-600"
              >
                <FiX />
              </button>
            )}
          </div>

          {/* Filter Dropdowns */}
          <div className="flex items-center gap-3 w-full md:w-auto">
            {/* User Type Filter */}
            <select
              value={userTypeFilter}
              onChange={(e) => {
                setUserTypeFilter(e.target.value);
                setPageNo(1);
              }}
              className="h-11 px-3.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
            >
              <option value="">All User Types</option>
              <option value="admin">Admin</option>
              <option value="partner">Partner</option>
              <option value="user">User</option>
              <option value="supervisor">Supervisor</option>
            </select>

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPageNo(1);
              }}
              className="h-11 px-3.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-700 outline-none focus:ring-2 focus:ring-blue-500 cursor-pointer"
            >
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/80 border-b border-gray-200 text-xs uppercase font-semibold text-gray-500 tracking-wider">
                  <th className="p-4 pl-6">#</th>
                  <th className="p-4">User</th>
                  <th className="p-4">Contact</th>
                  <th className="p-4">Type / Role</th>
                  <th className="p-4">Organization</th>
                  <th className="p-4">Status</th>
                  <th className="p-4 pr-6 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm">
                {usersList.length === 0 ? (
                  <tr>
                    <td
                      colSpan="7"
                      className="p-8 text-center text-gray-400 font-medium"
                    >
                      No users found matching your criteria.
                    </td>
                  </tr>
                ) : (
                  usersList.map((u, index) => {
                    const initials = (u.fullName || u.email || "U")
                      .split(" ")
                      .map((n) => n[0])
                      .join("")
                      .toUpperCase()
                      .slice(0, 2);

                    return (
                      <tr
                        key={u.id}
                        className="hover:bg-blue-50/40 transition duration-150"
                      >
                        <td className="p-4 pl-6 text-gray-400 font-mono text-xs">
                          {(pageNo - 1) * pageSize + index + 1}
                        </td>

                        {/* Name & Email */}
                        <td className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-500 text-white font-semibold flex items-center justify-center text-xs shadow-sm">
                              {initials}
                            </div>
                            <div>
                              <div className="font-semibold text-gray-800">
                                {u.fullName || "Unnamed User"}
                              </div>
                              <div className="text-xs text-gray-500 font-mono">
                                {u.email}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Mobile */}
                        <td className="p-4 text-gray-600 font-medium">
                          {u.mobileNo ? `+91 ${u.mobileNo}` : "—"}
                        </td>

                        {/* Role & UserType Badges */}
                        <td className="p-4">
                          <div className="flex flex-wrap gap-1.5">
                            <span
                              className={`px-2.5 py-0.5 rounded-full text-xs font-semibold capitalize ${
                                u.userType === "admin"
                                  ? "bg-purple-100 text-purple-700 border border-purple-200"
                                  : u.userType === "partner"
                                  ? "bg-amber-100 text-amber-700 border border-amber-200"
                                  : "bg-blue-100 text-blue-700 border border-blue-200"
                              }`}
                            >
                              {u.userType || "user"}
                            </span>
                            {u.role && u.role !== u.userType && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200 capitalize">
                                {u.role}
                              </span>
                            )}
                          </div>
                        </td>

                        {/* Organization & Designation */}
                        <td className="p-4">
                          <div className="text-gray-800 font-medium">
                            {u.organization || "—"}
                          </div>
                          {u.designation && u.designation !== "user" && (
                            <div className="text-xs text-gray-500 capitalize">
                              {u.designation}
                            </div>
                          )}
                        </td>

                        {/* Status */}
                        <td className="p-4">
                          <span
                            className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
                              u.disabled
                                ? "bg-red-100 text-red-700 border border-red-200"
                                : "bg-emerald-100 text-emerald-700 border border-emerald-200"
                            }`}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${
                                u.disabled ? "bg-red-500" : "bg-emerald-500"
                              }`}
                            />
                            {u.disabled ? "Disabled" : "Active"}
                          </span>
                        </td>

                        {/* Action Buttons */}
                        <td className="p-4 pr-6">
                          <div className="flex items-center justify-center gap-2">
                            {/* Edit Profile */}
                            <button
                              onClick={() => setEditProfileUser(u)}
                              title="Edit User Profile"
                              className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition"
                            >
                              <FiEdit className="text-base" />
                            </button>

                            {/* Change Password */}
                            <button
                              onClick={() => setChangePasswordUser(u)}
                              title="Change Password"
                              className="p-2 text-indigo-600 hover:bg-indigo-100 rounded-lg transition"
                            >
                              <FiLock className="text-base" />
                            </button>

                            {/* Enable/Disable Toggle */}
                            <button
                              onClick={() => handleToggleStatus(u)}
                              title={u.disabled ? "Enable Account" : "Disable Account"}
                              className={`p-2 rounded-lg transition ${
                                u.disabled
                                  ? "text-emerald-600 hover:bg-emerald-100"
                                  : "text-red-500 hover:bg-red-100"
                              }`}
                            >
                              {u.disabled ? (
                                <FiUserCheck className="text-base" />
                              ) : (
                                <FiUserX className="text-base" />
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="p-4 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-gray-500">
            <div>
              Showing{" "}
              <span className="font-semibold text-gray-700">
                {usersList.length > 0 ? (pageNo - 1) * pageSize + 1 : 0}
              </span>{" "}
              to{" "}
              <span className="font-semibold text-gray-700">
                {Math.min(pageNo * pageSize, usersCount)}
              </span>{" "}
              of <span className="font-semibold text-gray-700">{usersCount}</span> users
            </div>

            <Pagination
              count={Math.ceil(usersCount / pageSize) || 1}
              page={pageNo}
              onChange={(e, val) => setPageNo(val)}
              color="primary"
              shape="rounded"
            />
          </div>
        </div>
      </motion.div>

      {/* Change Password Modal */}
      {changePasswordUser && (
        <ChangePasswordModal
          user={changePasswordUser}
          onClose={() => setChangePasswordUser(null)}
          onSuccess={() => {
            setChangePasswordUser(null);
            getAllUsers();
          }}
        />
      )}

      {/* Edit Profile Modal */}
      {editProfileUser && (
        <EditProfileModal
          user={editProfileUser}
          onClose={() => setEditProfileUser(null)}
          onSuccess={() => {
            setEditProfileUser(null);
            getAllUsers();
          }}
        />
      )}

      {loading && <LoadingComponent />}
    </div>
  );
}

// Change Password Modal Component
function ChangePasswordModal({ user, onClose, onSuccess }) {
  const { token } = useAuth();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      return toast.error("Password must be at least 6 characters long");
    }
    if (newPassword !== confirmPassword) {
      return toast.error("Passwords do not match");
    }

    try {
      setLoading(true);
      const { data } = await axios.post(
        `${server}/auth/changePassword`,
        {
          userId: user.id,
          newPassword,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(data?.message || "Password changed successfully!");
      onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-3xl shadow-2xl p-6 w-full max-w-md border border-gray-100"
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-indigo-100 text-indigo-600 rounded-xl">
              <IoKeyOutline className="text-2xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">Change Password</h2>
              <p className="text-xs text-gray-500">{user.fullName || user.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <FiX className="text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">
              New Password
            </label>
            <input
              type="password"
              required
              minLength={6}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minimum 6 characters"
              className="w-full h-11 px-4 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">
              Confirm New Password
            </label>
            <input
              type="password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Re-enter new password"
              className="w-full h-11 px-4 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-indigo-500 bg-gray-50 text-sm"
            />
          </div>

          <div className="flex gap-3 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="w-1/2 h-11 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-1/2 h-11 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold shadow-md transition disabled:opacity-50"
            >
              {loading ? "Updating..." : "Update Password"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// Edit Profile Modal Component
function EditProfileModal({ user, onClose, onSuccess }) {
  const { token } = useAuth();
  const [formData, setFormData] = useState({
    fullName: user.fullName || "",
    mobileNo: user.mobileNo || "",
    role: user.role || "user",
    userType: user.userType || "user",
    designation: user.designation || "",
    organization: user.organization || "",
    isOrganization: !!user.isOrganization,
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const { data } = await axios.put(
        `${server}/auth/updateUser`,
        {
          id: user.id,
          fullName: formData.fullName,
          mobileNo: Number(formData.mobileNo) || null,
          role: formData.role,
          userType: formData.userType,
          designation: formData.designation,
          organization: formData.organization,
          isOrganization: formData.isOrganization,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(data?.message || "User profile updated successfully!");
      onSuccess();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-3xl shadow-2xl p-6 w-full max-w-lg border border-gray-100 max-h-[90vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-blue-100 text-blue-600 rounded-xl">
              <FiEdit className="text-2xl" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">Update Profile</h2>
              <p className="text-xs text-gray-500">{user.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <FiX className="text-xl" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">
              Full Name
            </label>
            <input
              type="text"
              required
              value={formData.fullName}
              onChange={(e) =>
                setFormData({ ...formData, fullName: e.target.value })
              }
              placeholder="Enter full name"
              className="w-full h-11 px-4 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">
              Mobile Number
            </label>
            <input
              type="tel"
              value={formData.mobileNo}
              onChange={(e) =>
                setFormData({ ...formData, mobileNo: e.target.value })
              }
              placeholder="10-digit mobile number"
              className="w-full h-11 px-4 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">
                User Type
              </label>
              <select
                value={formData.userType}
                onChange={(e) =>
                  setFormData({ ...formData, userType: e.target.value })
                }
                className="w-full h-11 px-3 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 text-sm cursor-pointer"
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
                <option value="partner">Partner</option>
                <option value="supervisor">Supervisor</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">
                Role
              </label>
              <input
                type="text"
                value={formData.role}
                onChange={(e) =>
                  setFormData({ ...formData, role: e.target.value })
                }
                placeholder="e.g. admin, manager"
                className="w-full h-11 px-4 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">
              Designation
            </label>
            <input
              type="text"
              value={formData.designation}
              onChange={(e) =>
                setFormData({ ...formData, designation: e.target.value })
              }
              placeholder="e.g. Lead Engineer, Project Manager"
              className="w-full h-11 px-4 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 text-sm"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase mb-1">
              Organization
            </label>
            <input
              type="text"
              value={formData.organization}
              onChange={(e) =>
                setFormData({ ...formData, organization: e.target.value })
              }
              placeholder="Company or Department name"
              className="w-full h-11 px-4 border border-gray-300 rounded-xl outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 text-sm"
            />
          </div>

          <div className="flex items-center gap-2 pt-1">
            <input
              type="checkbox"
              id="isOrg"
              checked={formData.isOrganization}
              onChange={(e) =>
                setFormData({ ...formData, isOrganization: e.target.checked })
              }
              className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
            />
            <label htmlFor="isOrg" className="text-sm text-gray-700 cursor-pointer">
              Is this an Organization Account?
            </label>
          </div>

          <div className="flex gap-3 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="w-1/2 h-11 border border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-100 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="w-1/2 h-11 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold shadow-md transition disabled:opacity-50"
            >
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
